/** Caption cues: generate from the voiceover, edit wording and timing, export SRT / VTT. */

import type { Subtitle } from "@studio/engine";
import { Loader2, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { api, fileUrl, type Job } from "../api";
import { appUrl } from "../base";
import { flush } from "./save";
import { uid, useEditor } from "./store";

/** Run the transcription job and apply the result (one undo step). Cues whose voiceover hasn't
 * changed are kept as they are, hand edits included, unless `replace`. */
export async function generateSubtitles(onProgress: (j: Job) => void, replace = false): Promise<{ cues: number; kept: number }> {
  const p = useEditor.getState().project!;
  await flush();
  let job = await api.generateSubtitles(p.id, replace);
  while (["queued", "running"].includes(job.status)) {
    onProgress(job);
    await new Promise((r) => setTimeout(r, 800));
    job = await api.job(job.id);
  }
  if (job.status !== "done" || !job.output?.file) throw new Error(job.message || "transcription failed");
  const cues = (await (await fetch(fileUrl(p.id, job.output.file))).json()) as Subtitle[];
  useEditor.getState().edit((d) => void (d.subtitles = cues));
  return { cues: cues.length, kept: job.output.kept ?? 0 };
}

export function SubtitlesDialog() {
  const project = useEditor((s) => s.project)!;
  const cues = [...project.subtitles].sort((a, b) => a.t0 - b.t0);
  const [busy, setBusy] = useState<Job | null>(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const close = () => useEditor.getState().set({ panel: null });
  const edit = (id: string, fn: (c: Subtitle) => void) =>
    useEditor.getState().edit((d) => {
      const c = d.subtitles.find((x) => x.id === id);
      if (c) fn(c);
    });
  const run = async (replace = false) => {
    if (replace && !confirm("Rebuild every cue from the voiceover? Your edits to the captions will be lost (undo brings them back).")) return;
    setErr("");
    setNote("");
    try {
      const r = await generateSubtitles((j) => setBusy(j), replace);
      setNote(replace || !r.kept ? `${r.cues} cues.` : r.cues === r.kept ? `Nothing to change: the voiceover under all ${r.cues} cues is unchanged.` : `${r.cues - r.kept} cue${r.cues - r.kept === 1 ? "" : "s"} regenerated where the voiceover changed; ${r.kept} kept as they were.`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const add = () => {
    const t = useEditor.getState().time;
    useEditor.getState().edit((d) => void d.subtitles.push({ id: uid("c"), t0: +t.toFixed(3), t1: +(t + 2.5).toFixed(3), text: "New caption", asset: null, at: null }));
  };
  const seek = (t: number) => window.dispatchEvent(new CustomEvent("studio:seek", { detail: t + 0.05 }));

  return (
    <div className="modal-back" onPointerDown={close}>
      <div className="modal subtitles" onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <h3>Captions ({cues.length} cues)</h3>
          <button className="icon" onClick={close}><X size={18} /></button>
        </header>
        <div className="analysis-actions" style={{ justifyContent: "flex-start" }}>
          <button className="primary" disabled={!!busy} onClick={() => run(false)} title={cues.length ? "Transcribe the voice clips and update the captions. Cues whose voiceover hasn't changed stay as they are, your edits included." : "Transcribe every voice clip and build timed captions"}>
            {busy ? <><Loader2 size={14} className="spin" /> {busy.message || "Transcribing"} {Math.round(busy.progress * 100)}%</> : cues.length ? "Update from voiceover" : "Generate from voiceover"}
          </button>
          {cues.length > 0 && <button disabled={!!busy} onClick={() => run(true)} title="Rebuild every cue from the voiceover, discarding edits">Start over</button>}
          <button onClick={add}><Plus size={14} /> Cue at playhead</button>
          <a className="button" href={appUrl(`/api/projects/${project.id}/captions.srt`)}>Download .srt</a>
          <a className="button" href={appUrl(`/api/projects/${project.id}/captions.vtt`)}>Download .vtt</a>
          {cues.length > 0 && <button onClick={() => useEditor.getState().edit((d) => void (d.subtitles = []))}>Clear</button>}
        </div>
        {err && <p className="bad">{err}</p>}
        {note && <p className="good small">{note}</p>}
        <p className="muted small">
          {project.captions.enabled ? "Captions are burned into the video." : "Burn-in is off (project settings); the .srt/.vtt files are still written next to every render."}
          {" "}Without cues, each scene's caption text is used.
        </p>
        <div className="import-table cues">
          <table>
            <thead><tr><th>Start</th><th>End</th><th>Text</th><th /></tr></thead>
            <tbody>
              {cues.map((c) => (
                <tr key={c.id}>
                  <td><input className="num short" type="number" step="0.05" value={c.t0} onChange={(e) => edit(c.id, (x) => void (x.t0 = +e.target.value))} onFocus={() => seek(c.t0)} /></td>
                  <td><input className="num short" type="number" step="0.05" value={c.t1} onChange={(e) => edit(c.id, (x) => void (x.t1 = +e.target.value))} /></td>
                  <td><CueText cue={c} onCommit={(v) => edit(c.id, (x) => void (x.text = v))} onFocus={() => seek(c.t0)} /></td>
                  <td><button className="icon" title="Delete cue" onClick={() => useEditor.getState().edit((d) => void (d.subtitles = d.subtitles.filter((x) => x.id !== c.id)))}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CueText({ cue, onCommit, onFocus }: { cue: Subtitle; onCommit: (v: string) => void; onFocus: () => void }) {
  const [v, setV] = useState(cue.text);
  return <textarea rows={2} value={v} onFocus={onFocus} onChange={(e) => setV(e.target.value)} onBlur={() => v !== cue.text && onCommit(v)} />;
}
